import axios from "axios";
import React, { Dispatch, SetStateAction } from "react";
import { SharedAccess, UserModelRole } from "target_wasm";
import {
  ShareOutcome,
  RemoveAccessData,
  Permission,
  RefetchOptions,
  ResourceType,
} from "components/shareSection/entities";
import { AccessPanel } from "./AccessPanel";
import { SpaceAccess } from "./useModelSpaceAccess";
import { showNotificationGlobal } from "stores/useNotificationStore";

export interface ShareSuccessContext {
  isRevoke: boolean;
}

interface ShareSectionProps {
  sharedWithList: SharedAccess | undefined;
  setSharedWithList?: Dispatch<SetStateAction<SharedAccess | undefined>>;
  entityId: string;
  resourceType: ResourceType;
  canShare: boolean;
  shareDisabled?: boolean;
  canRevokeAccess: boolean;
  onShare: (
    emails: string[],
    roleOrPermission: UserModelRole | Permission | null,
  ) => Promise<ShareOutcome | void>;
  onRevokeAccess: (data: RemoveAccessData | null) => Promise<void>;
  onSuccess?: (context: ShareSuccessContext) => void;
  onError?: (error: string, status: number) => void;
  defaultRole?: UserModelRole;
  defaultPermission?: Permission;
  onRefetchSharedList?: (options?: RefetchOptions) => Promise<boolean | void>;
  userContinuationToken?: string | null;
  onResendInvitation?: (email: string) => Promise<void>;
  space?: SpaceAccess;
  viewerRole?: string;
  notice?: React.ReactNode;
  shareOptions?: React.ReactNode;
}

export const ShareSection: React.FC<ShareSectionProps> = (props) => {
  const handleRevokeAccess = async (data: RemoveAccessData) => {
    try {
      await props.onRevokeAccess(data);
      props.onSuccess?.({ isRevoke: true });
      const message = data.isPending
        ? "Invitation cancelled"
        : "Access revoked successfully";
      showNotificationGlobal(message, "success");
    } catch (error: unknown) {
      let rawMessage = "Failed to revoke access";
      let status = 500;
      if (axios.isAxiosError(error)) {
        rawMessage =
          (error.response?.data as string) ||
          error.message ||
          "Failed to revoke access";
        status = error.response?.status || 500;
      } else if (error instanceof Error) {
        rawMessage = error.message;
      }
      props.onError?.(rawMessage, status);
      showNotificationGlobal("Failed to revoke access", "error");
    }
  };

  const handleUserShare = async (
    emails: string[],
    role: UserModelRole | Permission | null,
  ) => {
    try {
      // Hand the outcome back up — swallowing it here is what kept
      // capacity/refusal warnings from ever reaching the dialog.
      const outcome = await props.onShare(emails, role);
      props.onSuccess?.({ isRevoke: false });
      return outcome;
    } catch (error: unknown) {
      let message = "Failed to share";
      let status = 500;
      if (axios.isAxiosError(error)) {
        message = error.message || "Failed to share";
        status = error.response?.status || 500;
      } else if (error instanceof Error) {
        message = error.message;
      }
      props.onError?.(message, status);
      // Rethrown, or EmailSection's success branch runs on a failed share.
      throw error;
    }
  };

  return (
    <div className="flex flex-col grow min-h-0 w-full space-y-4 overflow-visible">
      <AccessPanel
        entityId={props.entityId}
        resourceType={props.resourceType}
        notice={props.notice}
        shareOptions={props.shareOptions}
        sharedWithList={props.sharedWithList}
        setSharedWithList={props.setSharedWithList}
        canShare={props.canShare}
        shareDisabled={props.shareDisabled}
        canRevokeAccess={props.canRevokeAccess}
        defaultRole={props.defaultRole}
        defaultPermission={props.defaultPermission}
        continuationToken={props.userContinuationToken}
        onRefetchSharedList={props.onRefetchSharedList}
        onShare={handleUserShare}
        onRevokeAccess={handleRevokeAccess}
        onResendInvitation={props.onResendInvitation}
        space={props.space}
        viewerRole={props.viewerRole}
      />
    </div>
  );
};
